import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { NotificationApi } from "../../apis/Notification.api";
import { toast } from "react-toastify";
import LoaderContext from "../loader";
import { useSocket } from "./SocketProvider";

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

const NotificationProvider = ({ children }) => {
  const [notificationData, setNotificationData] = useState([]);
  const { handleLoading } = useContext(LoaderContext);
  const socket = useSocket(); // ✅ directly get socket instance
  const user = useSelector((state) => state.store.currentUser);
  const userId = user?._id;

  // 🔹 Join personal notification room
  useEffect(() => {
    if (userId && socket) {
      console.log("Socket from context:", socket);
      socket.emit("join-notification-room", { userId });
      console.log("Joined notification room for user:", userId);
    }
  }, [socket, userId]);

  // 🔹 Listen for notifications & order cancellations
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data) => {
      console.log("Received notification:", data);
      toast.info(`${data.title}: ${data.message}`, { autoClose: 4000 });
      setNotificationData((prev) => [data, ...prev]);
    };

    const handleOrderCancellation = (data) => {
      console.log("Order cancelled notification:", data);
      const notification = {
        title: "Order Cancelled",
        message: `Order #${data.orderId} has been cancelled by the customer`,
        type: "order_cancelled",
        orderId: data.orderId,
        reason: data.reason,
        createdAt: new Date(),
        is_read: false,
      };
      toast.error(`Order Cancelled: ${notification.message}`, {
        autoClose: 5000,
      });
      setNotificationData((prev) => [notification, ...prev]);

      // Save to backend as well
      saveNotificationToBackend(notification);
    };

    socket.on("notification", handleNotification);
    socket.on("order-cancelled", handleOrderCancellation);

    return () => {
      socket.off("notification", handleNotification);
      socket.off("order-cancelled", handleOrderCancellation);
    };
  }, [socket]);

  // 🔹 Save notification to backend
  const saveNotificationToBackend = async (notification) => {
    try {
      await NotificationApi.createNotification(notification);
    } catch (error) {
      console.error("Failed to save notification to backend:", error);
    }
  };

  // 🔹 Fetch all notifications
  const getAllNotification = useCallback(async () => {
    try {
      handleLoading(true);
      const res = await NotificationApi.getAllNotification();
      setNotificationData(res?.data?.data?.allNotifications || []);
    } catch (error) {
      toast.error("Failed to fetch notifications");
    } finally {
      handleLoading(false);
    }
  }, [handleLoading]);

  // 🔹 Mark one as read
  const updateNotification = useCallback(
    async (id) => {
      try {
        handleLoading(true);
        await NotificationApi.updateNotification(id, { is_read: true });
        setNotificationData((prev) =>
          prev.map((n) => (n._id === id ? { ...n, is_read: true } : n))
        );
      } catch (error) {
        toast.error("Failed to update notification");
      } finally {
        handleLoading(false);
      }
    },
    [handleLoading]
  );

  // 🔹 Mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      handleLoading(true);
      await NotificationApi.updateAllNotification();
      setNotificationData((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
    } catch (error) {
      toast.error("Failed to mark all notifications as read");
    } finally {
      handleLoading(false);
    }
  }, [handleLoading]);

  const value = useMemo(
    () => ({
      notificationData,
      getAllNotification,
      updateNotification,
      handleMarkAllAsRead,
      setNotificationData,
    }),
    [
      notificationData,
      getAllNotification,
      updateNotification,
      handleMarkAllAsRead,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
